import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'en' | 'es' | 'fr';

export interface LanguageConfig {
    code: Language;
    name: string;
    nativeName: string;
    flag: string;
    countryCode: string;
}

export const LANGUAGES: Record<Language, LanguageConfig> = {
    en: { code: 'en', name: 'English',  nativeName: 'English',  flag: '🇺🇸', countryCode: 'US' },
    es: { code: 'es', name: 'Spanish',  nativeName: 'Español',  flag: '🇪🇸', countryCode: 'ES' },
    fr: { code: 'fr', name: 'French',   nativeName: 'Français', flag: '🇫🇷', countryCode: 'FR' },
};

// ─── URL helpers ─────────────────────────────────────────────────────────────

/**
 * Extract the locale prefix from a pathname, e.g. '/es/faq' → 'es'.
 * Returns null for English (no prefix) or unrecognised prefixes.
 */
export function getLangFromPath(pathname: string): Language | null {
    const seg = pathname.split('/')[1];
    if (seg === 'es' || seg === 'fr') return seg;
    return null;
}

/**
 * Strip the locale prefix from a pathname.
 * '/es/learn-more' → '/learn-more'
 * '/learn-more'    → '/learn-more'
 */
export function stripLangPrefix(pathname: string): string {
    const lang = getLangFromPath(pathname);
    if (!lang) return pathname;
    const stripped = pathname.slice(1 + lang.length) || '/';
    return stripped.startsWith('/') ? stripped : '/' + stripped;
}

/**
 * Add a locale prefix to a pathname.
 * ('es', '/learn-more') → '/es/learn-more'
 * ('en', '/learn-more') → '/learn-more'
 */
export function addLangPrefix(lang: Language, pathname: string): string {
    if (lang === 'en') return pathname;
    const bare = stripLangPrefix(pathname);
    return `/${lang}${bare === '/' ? '' : bare}`;
}

/**
 * Returns true for public marketing pages that should get a language prefix.
 * Private/auth/admin paths are left alone.
 */
export function isPublicPage(pathname: string): boolean {
    const bare = stripLangPrefix(pathname);
    const PRIVATE_PREFIXES = [
        '/admin', '/customer', '/application', '/co-applicant',
        '/bank', '/verify', '/diligence', '/system', '/simulator',
        '/calculator', '/api-explorer', '/database-standalone',
        '/styles', '/components', '/invitation',
    ];
    return !PRIVATE_PREFIXES.some(p => bare.startsWith(p));
}

// ─── Initial language detection ──────────────────────────────────────────────

const detectBrowserLanguage = (): Language => {
    const browserLang = navigator.language || (navigator as any).userLanguage || 'en';
    const primary = browserLang.split('-')[0].toLowerCase();
    if (primary === 'fr') return 'fr';
    if (primary === 'es') return 'es';
    return 'en';
};

/**
 * DETERMINISTIC initial language — derived ONLY from the URL path.
 *
 * Hydration safety (the whole point of this change):
 *   The pre-renderer (Puppeteer) and the browser's first render must produce
 *   IDENTICAL output, or React discards the pre-rendered DOM and re-renders
 *   from scratch — which is exactly the LCP penalty that caused pre-rendering
 *   to be disabled. The URL is the ONE input both environments agree on:
 *   Puppeteer renders '/es/faq' and the browser loads '/es/faq' → both see 'es'.
 *
 *   localStorage and navigator.language are browser-only. Reading them here
 *   (as the old getInitialLanguage did) makes the first client render depend
 *   on state the pre-renderer never had → guaranteed mismatch. So those inputs
 *   are deferred to a post-hydration useEffect below, mirroring the QuoteStrip
 *   pattern (deterministic first paint, dynamic swap after mount).
 *
 *   For an un-prefixed URL we ALWAYS return 'en'. That matches the pre-rendered
 *   '/', '/faq', '/blog/...' snapshots, which are captured in English. A
 *   returning Spanish/French user is corrected one tick later by the effect.
 */
const getInitialLanguage = (): Language => {
    const fromUrl = getLangFromPath(window.location.pathname);
    return fromUrl ?? 'en';
};

/**
 * Resolve the language a returning visitor SHOULD see, using browser-only
 * signals. Called from useEffect AFTER hydration — never during render.
 * Returns null when no redirect/correction is needed.
 */
const resolvePreferredLanguage = (): Language | null => {
    // URL prefix already won during initial render; nothing to correct.
    if (getLangFromPath(window.location.pathname)) {
        localStorage.setItem('preferred_language', getLangFromPath(window.location.pathname)!);
        localStorage.setItem('language_detected', 'true');
        return null;
    }

    const stored = localStorage.getItem('preferred_language') as Language | null;
    if (stored === 'en' || stored === 'es' || stored === 'fr') return stored;

    if (!localStorage.getItem('language_detected')) {
        const detected = detectBrowserLanguage();
        localStorage.setItem('preferred_language', detected);
        localStorage.setItem('language_detected', 'true');
        return detected;
    }

    return null;
};

// ─── Context ─────────────────────────────────────────────────────────────────

interface LanguageContextType {
    language: Language;
    /**
     * Switch language. On public pages this navigates to the new prefix URL.
     * On private/auth pages it only updates the stored preference.
     */
    setLanguage: (lang: Language, currentPathname?: string) => void;
    languageConfig: LanguageConfig;
    /** Current path WITHOUT the language prefix, e.g. '/faq' */
    barePath: string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [language, setLanguageState] = useState<Language>(getInitialLanguage);

    const barePath = stripLangPrefix(window.location.pathname);

    // ── Post-hydration language correction (runs once, after mount) ───────────
    // The initial render is deterministic (URL-only) so it matches the
    // pre-rendered HTML. AFTER hydration we consult localStorage / browser
    // language and, if a returning visitor prefers a non-English locale on a
    // public page, navigate them to the prefixed URL. Because this fires post-
    // hydration, it never causes a mismatch — by the time it runs, React has
    // already adopted the pre-rendered DOM.
    useEffect(() => {
        const preferred = resolvePreferredLanguage();
        if (!preferred) return;

        if (preferred !== language) setLanguageState(preferred);

        if (preferred !== 'en' && isPublicPage(window.location.pathname)
            && !getLangFromPath(window.location.pathname)) {
            const prefixed = addLangPrefix(preferred, window.location.pathname);
            window.history.replaceState(null, '', prefixed + window.location.search);
        }
        // Intentionally run once on mount only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setLanguage = (lang: Language, currentPathname?: string) => {
        const pathname = currentPathname ?? window.location.pathname;
        localStorage.setItem('preferred_language', lang);

        if (isPublicPage(pathname)) {
            const bare = stripLangPrefix(pathname);
            const newPath = addLangPrefix(lang, bare);
            // Full navigation so React Router picks up the new URL
            window.location.assign(newPath + window.location.search);
            return;
        }

        // Private page — just update state, no navigation
        setLanguageState(lang);
    };

    // Keep document.lang in sync
    useEffect(() => {
        document.documentElement.lang = language;
    }, [language]);

    return (
        <LanguageContext.Provider
            value={{ language, setLanguage, languageConfig: LANGUAGES[language], barePath }}
        >
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
    return context;
};